import { useState } from 'react'
import { useAtom } from 'jotai'
import Button from '@mui/material/Button'
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import Alert from '@mui/material/Alert'
import dayjs, { type Dayjs }  from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/cs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'

import type { YearlyFeePaymentsGenerationResponse } from '@/types'
import { yearlyFeePaymentsGenerationIdAtom } from '@/state'
import { useGenerateYearlyFeePayments } from '@/hooks/api/use-yearly-fee-collection'
import NumberTextfield from '../form-fields/number-textfield'

export default function YearlyFeePaymentsGeneration({
    yearlyFeeCollectionId,
}: {
    yearlyFeeCollectionId: number,
}) {

    const [yearlyFeePaymentsGenerationId, setYearlyFeePaymentsGenerationId] = useAtom(yearlyFeePaymentsGenerationIdAtom)
    const [requestInProgress, setRequestInProgress] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)

    // Action dialogs
    const [confirm, setConfirm] = useState<boolean>(false)

    const [dueDate, setDueDate] = useState<Dayjs>(dayjs())
    const [requestedAmountRatioPercent, setRequestedAmountRatioPercent] = useState<string>('100')
    const [requestRestOfFeeFromPayers, setRequestRestOfFeeFromPayers] = useState<boolean>(false)

    const successCallback = (data: YearlyFeePaymentsGenerationResponse) => {
        if (data.id) {
            setYearlyFeePaymentsGenerationId(data.id)
        }
    }

    const mutation = useGenerateYearlyFeePayments(
        yearlyFeeCollectionId,
        {
            onSuccessCallback: successCallback,
            setRequestInProgress,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            },
        },
    )

    const handleClickGenerate = () => {
        setRequestInProgress(true)
        mutation.mutate({
            dueDate,
            requestedAmountRatioPercent,
            requestRestOfFeeFromPayers,
        })
        setConfirm(false)
    }

    return (
        <>
            <Button
                color="terciary"
                variant="outlined"
                startIcon={<ReceiptRoundedIcon />}
                onClick={() => setConfirm(true)}
                disabled={requestInProgress || !!yearlyFeePaymentsGenerationId}
                sx={{
                    backgroundColor: 'errorRedLight.light',
                    borderColor: 'errorRedLight.dark',
                }}
            >
                2. Vygenerovat předpisy plateb
            </Button>
            <Dialog
                open={confirm}
                onClose={() => setConfirm(false)}
            >
                <DialogContent>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        {error &&
                            <Alert
                                severity="success"
                                sx={(theme) => ({
                                    flexGrow: 1,
                                    margin: theme.spacing(1, 0),
                                })}
                                action={
                                    <Button
                                        color="success"
                                        size="small"
                                        onClick={() => setError(null)}
                                    >
                                        Zavřít
                                    </Button>
                                }
                            >
                                {error}
                            </Alert>
                        }
                        <Typography variant="h5" component="div" mb={2}>
                            Vygenerování předpisu plateb
                        </Typography>
                        <Typography variant="body2" component="div" mb={2}>
                            Tato akce přiřadí předpis všem poplatníkům. Nastavte prosím parametry
                        </Typography>
                        <Box sx={{
                            p: 2,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            justifyContent: 'center',
                        }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="cs">
                                <DatePicker
                                    label="Datum splatnosti"
                                    views={['year', 'month', 'day']}
                                    value={dueDate}
                                    onChange={newDate => {
                                        setDueDate(newDate)
                                    }}
                                    slotProps={{
                                        textField: { required: true },
                                    }}
                                />
                            </LocalizationProvider>
                            <NumberTextfield
                                label="Procento z ročního poplatku"
                                onChange={(e) => setRequestedAmountRatioPercent(e.target.value)}
                                name='requestedAmountRatioPercent'
                                value={requestedAmountRatioPercent}
                                endAdornment="%"
                                thousandSeparator
                                decimalScale={2}
                                disabled={requestRestOfFeeFromPayers}
                            />
                            <Box>
                                <FormControlLabel control={
                                    <Switch
                                        checked={requestRestOfFeeFromPayers}
                                        onChange={(e) => setRequestRestOfFeeFromPayers(e.target.checked)}
                                    />
                                } label="Celý zbylý doplatek" />
                            </Box>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            flexGrow: 1,
                        }}>
                            <Button
                                variant="text"
                                color="terciary"
                                onClick={() => setConfirm(false)}
                            >
                                Zrušit
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={requestInProgress ? <CircularProgress color="inherit" size={20} /> : <CheckCircleRoundedIcon />}
                                onClick={() => handleClickGenerate()}
                                disabled={requestInProgress || !dueDate || (!requestRestOfFeeFromPayers && !requestedAmountRatioPercent)}
                            >
                                Vygenerovat
                            </Button>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    )
}
